import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'src/components/atoms';
import { StyledIcon } from 'src/components/foundations/foundations.styles';
import { Accordion } from 'src/components/molecules';
import { AccordionTitle } from 'src/components/molecules/Accordion/Accordion.styles';
import {
  EventDetailsVm,
  eventDetailsVm,
  toggleShowAddNamedOffenderModal,
  translationModalOpenStateUpdated,
} from 'src/components/pages/Case/Case.slice';
import { hiddenDots } from 'src/components/pages/Case/helpers/hiddenDots';
import { FormattedNamedOffender } from 'src/components/pages/Case/helpers/namedOffender';
import { CallStep } from 'src/graphql';
import { useTheme } from 'styled-components';
import { SUBMISSION_MODE, UploadScanStatus } from 'src/types';
import { stripFileExtension } from 'src/utils';
import { UploadedEvidenceFile } from '../Reporting/ReportingReport/UploadedEvidence';
import EventAudio from './EventAudio';
import {
  EventDetailsButton,
  EventDetailsContainer,
  EventDetailsDescription,
  EventDetailsGrid,
  EventDetailsGridTitle,
  EventDetailsSubtitle,
  EventDetailsTitle,
  EventDetailsTitleAndButtonContainer,
  EventDetailsTranslateContainer,
  EventHeader,
  NamedOffenderHeader,
  TranscriptionDisclaimer,
} from './EventDetailsAccordion.styles';
import { formatOffenders } from './formatOffenders';
import { UploadedEvidenceSection } from './UploadedEvidenceSection';

interface Props {
  eventId: string;
  reportId: string;
  defaultOpen?: boolean;
  last?: boolean; // set to true if it's a single accordion or the last of multiple
  'data-testid'?: string;
  isFirstEvent?: boolean;
  isLastEvent?: boolean;
  isDominantLanguageKnown?: boolean;
  testVm?: EventDetailsVm;
}

export const EventDetailsAccordion = ({
  eventId,
  reportId,
  defaultOpen,
  last,
  isDominantLanguageKnown,
  testVm,
  ...props
}: Props) => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const {
    formattedEventId,
    dateCreated,
    submissionMode,
    dynamicTranslationsEnabled,
    showAudioRecordings,
    audioRecordings,
    canAddNamedOffender,
    namedOffenders,
    usersToExcludeInNamedOffenderModal,
    eventEvidence: {
      date,
      time,
      location,
      description,
      impact,
      witnesses,
      evidence,
    },
    showNamedOffenders,
    showAddNamedOffenderButton,
    canViewReportDetails,
  } = useSelector(eventDetailsVm({ eventId, reportId, testVm }));

  const isVaultTalk = submissionMode === SUBMISSION_MODE.VAULT_TALK;

  const onTranslateClick = () =>
    dispatch(translationModalOpenStateUpdated({ open: true }));

  const showTranslateButton =
    dynamicTranslationsEnabled && isDominantLanguageKnown;

  const getStepRecording = (step: CallStep) =>
    audioRecordings.find((recording) => recording.step === step) ?? '';

  const linkOffender = (
    offender: FormattedNamedOffender,
    canAddNamedOffender?: boolean,
  ) => {
    if (!canAddNamedOffender || !offender.external) {
      return '-';
    }

    return (
      <EventDetailsButton
        size="small"
        variant="secondary"
        data-testid={`${props['data-testid']}-named-offenders-${offender.name}-link-offender`}
        onClick={() => {
          dispatch(
            toggleShowAddNamedOffenderModal({
              open: true,
              usersToExclude: usersToExcludeInNamedOffenderModal,
              reportId,
              eventId,
              externalNamedOffenderToLink: offender.id,
            }),
          );
        }}
      >
        <StyledIcon size="16px" className="uil uil-link-add" />
        Link employee
      </EventDetailsButton>
    );
  };

  const uploadedEvidence: UploadedEvidenceFile[] = evidence
    .filter((ev) => ev.dataDeletedAt === null)
    .map((evidenceItem) => {
      const defaultScanStatus = UploadScanStatus.CLEAN;
      const scanStatus = evidenceItem.scanStatus as UploadScanStatus;
      const status = scanStatus ?? defaultScanStatus;

      return {
        size: evidenceItem.fileSize ?? undefined,
        dateAdded: new Date(evidenceItem.createdAt),
        status,
        fileExtension:
          evidenceItem.value.fileExtension ??
          evidenceItem.value.path.split('.')[1] ??
          '',
        fileS3Path: stripFileExtension(evidenceItem.value.path),
      };
    });

  const showErrorInHeader = Boolean(
    uploadedEvidence.find(
      (evidence) => evidence.status === UploadScanStatus.INFECTED,
    ),
  );

  const blankOrHiddenNamedOffender = showNamedOffenders ? '-' : hiddenDots;

  return (
    <Accordion
      topBorderRadius={props.isFirstEvent ? '6px' : '0px'}
      bottomBorderRadius={props.isLastEvent ? '6px' : '0px'}
      title={
        showErrorInHeader && canViewReportDetails ? (
          <EventHeader>
            <AccordionTitle>{formattedEventId}</AccordionTitle>
            <StyledIcon
              className="uil uil-exclamation-octagon"
              color={
                theme.isVaultVariant
                  ? theme.newPalette.red.R300
                  : theme.atlas.semantic.color.status.error.default
              }
              size="24px"
              data-testid={`${props['data-testid']}-upload-error-icon`}
            />
          </EventHeader>
        ) : (
          formattedEventId
        )
      }
      subtitle={dateCreated}
      defaultOpen={defaultOpen}
      last={last}
      data-testid={props['data-testid']}
    >
      <EventDetailsContainer
        first
        data-testid={`${props['data-testid']}-date-time-location`}
      >
        {isVaultTalk && (
          <TranscriptionDisclaimer
            data-testid={`${props['data-testid']}-transcription-disclaimer`}
          >
            Transcription generated by AI.
          </TranscriptionDisclaimer>
        )}
        <EventDetailsTitle>Event details</EventDetailsTitle>
        <EventDetailsSubtitle>DATE</EventDetailsSubtitle>
        <EventDetailsDescription margin>{date}</EventDetailsDescription>
        <EventAudio
          showAudioRecordings={showAudioRecordings}
          getStepRecording={getStepRecording}
          stepType={CallStep.Date}
          testId={`${props['data-testid']}-recording-date`}
        />
        <EventDetailsSubtitle>TIME</EventDetailsSubtitle>
        <EventDetailsDescription margin>{time}</EventDetailsDescription>
        <EventAudio
          showAudioRecordings={showAudioRecordings}
          getStepRecording={getStepRecording}
          stepType={CallStep.Time}
          testId={`${props['data-testid']}-recording-time`}
        />
        <EventDetailsSubtitle>LOCATION</EventDetailsSubtitle>
        <EventDetailsDescription
          data-testid={`${props['data-testid']}-location`}
        >
          {location}
        </EventDetailsDescription>
        <EventAudio
          showAudioRecordings={showAudioRecordings}
          getStepRecording={getStepRecording}
          stepType={CallStep.Location}
          testId={`${props['data-testid']}-recording-location`}
        />
      </EventDetailsContainer>
      <EventDetailsContainer
        data-testid={`${props['data-testid']}-description`}
      >
        <EventDetailsTitle>What happened</EventDetailsTitle>
        <EventDetailsDescription>{description}</EventDetailsDescription>
        <EventAudio
          showAudioRecordings={showAudioRecordings}
          getStepRecording={getStepRecording}
          stepType={
            getStepRecording(CallStep.Description)
              ? CallStep.Description
              : CallStep.Question
          }
          testId={`${props['data-testid']}-recording-description`}
        />
      </EventDetailsContainer>
      <EventDetailsContainer data-testid={`${props['data-testid']}-impact`}>
        <EventDetailsTitle>Emotional impact</EventDetailsTitle>
        <EventDetailsDescription>{impact}</EventDetailsDescription>
        <EventAudio
          showAudioRecordings={showAudioRecordings}
          getStepRecording={getStepRecording}
          stepType={CallStep.Emotion}
          testId={`${props['data-testid']}-recording-impact`}
        />
      </EventDetailsContainer>
      <EventDetailsContainer data-testid={`${props['data-testid']}-witnesses`}>
        <EventDetailsTitle>Witnesses</EventDetailsTitle>
        <EventDetailsDescription>{witnesses}</EventDetailsDescription>
        <EventAudio
          showAudioRecordings={showAudioRecordings}
          getStepRecording={getStepRecording}
          stepType={CallStep.Witness}
          testId={`${props['data-testid']}-recording-witnesses`}
        />
      </EventDetailsContainer>
      <EventDetailsContainer data-testid={`${props['data-testid']}-evidence`}>
        <UploadedEvidenceSection
          uploadedEvidence={uploadedEvidence}
          reportId={reportId}
          eventId={eventId}
          data-testid={props['data-testid']}
        />
      </EventDetailsContainer>
      <EventDetailsContainer
        last={!showTranslateButton && last}
        data-testid={`${props['data-testid']}-named-offenders`}
      >
        <EventDetailsTitleAndButtonContainer>
          <NamedOffenderHeader>
            <EventDetailsTitle>Named offender</EventDetailsTitle>
            {showAddNamedOffenderButton && (
              <EventDetailsButton
                size="small"
                variant="primary"
                data-testid={`${props['data-testid']}-add-named-offender`}
                onClick={() => {
                  dispatch(
                    toggleShowAddNamedOffenderModal({
                      open: true,
                      usersToExclude: usersToExcludeInNamedOffenderModal,
                      reportId,
                      eventId,
                    }),
                  );
                }}
              >
                <StyledIcon size="16px" className="uil uil-plus" />
                Named offender
              </EventDetailsButton>
            )}
          </NamedOffenderHeader>
        </EventDetailsTitleAndButtonContainer>
        <EventAudio
          showAudioRecordings={showAudioRecordings}
          getStepRecording={getStepRecording}
          stepType={CallStep.Perpetrator}
          testId={`${props['data-testid']}-recording-named-offenders`}
        />
        <EventDetailsGrid>
          <EventDetailsGridTitle>NAME</EventDetailsGridTitle>
          <EventDetailsGridTitle>DEPARTMENT</EventDetailsGridTitle>
          <EventDetailsGridTitle>SITE</EventDetailsGridTitle>
          <EventDetailsGridTitle>COUNTRY</EventDetailsGridTitle>
          <EventDetailsGridTitle>REPORT ID</EventDetailsGridTitle>
          <EventDetailsGridTitle>ADDED</EventDetailsGridTitle>
          <EventDetailsGridTitle>LINKED</EventDetailsGridTitle>
          {namedOffenders.length === 0 ? (
            <>
              <EventDetailsDescription
                data-testid={`${props['data-testid']}-no-named-offenders-name`}
                fullWidth
              >
                {blankOrHiddenNamedOffender}
              </EventDetailsDescription>
              <EventDetailsDescription
                data-testid={`${props['data-testid']}-no-named-offenders-report-id`}
                fullWidth
              >
                {blankOrHiddenNamedOffender}
              </EventDetailsDescription>
              <EventDetailsDescription
                data-testid={`${props['data-testid']}-no-named-offenders-added`}
                fullWidth
              >
                {blankOrHiddenNamedOffender}
              </EventDetailsDescription>
              <EventDetailsDescription
                data-testid={`${props['data-testid']}-no-named-offenders-linked`}
                fullWidth
              >
                {blankOrHiddenNamedOffender}
              </EventDetailsDescription>
            </>
          ) : (
            formatOffenders({
              namedOffenders,
              dataTestId: props['data-testid'] ?? '',
              linkOffender,
              canAddNamedOffender,
              theme,
            })
          )}
        </EventDetailsGrid>
      </EventDetailsContainer>
      {showTranslateButton && (
        <EventDetailsContainer last={last}>
          <EventDetailsTranslateContainer>
            <Button
              variant="secondary"
              onClick={onTranslateClick}
              data-testid={`${props['data-testid']}-translate-button`}
              data-intercom-target="translate-button"
            >
              Translate event details
            </Button>
          </EventDetailsTranslateContainer>
        </EventDetailsContainer>
      )}
    </Accordion>
  );
};
